import { Box, IconButton, Tooltip } from '@mui/material';
import { AnchorText } from 'components/Text';
import {
  Campaign,
  CampaignStatus,
  CampaignType
} from 'services/campaign/types';
import {
  GetCampaignsByCampaignGroupIdQuery,
  useGetCampaignsByCampaignGroupIdQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { mapUserCampaignsToCampaigns } from 'utils/generatedCampaignMappers';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Loading } from 'components/Loading';
import { SearchCampaignItem } from 'modules/campaigns/types/SearchCampaigns';
import { hasCampaignInReview } from '../utils/hasCampaignInReview';
import { CampaignListItem } from 'modules/campaigns/types/CampaignListItem';
import { WorkflowBuilderIcon } from 'common/icons/WorkflowBuilderIcon';
import { workflowBuilderLink } from 'modules/layout/constants/navbar';
import { Paths } from 'services/navigation/paths';
import {
  iconContainer,
  workflowIconButtonSx,
  workflowTooltipSx,
  workflowTooltipArrowSx
} from './campaignJobTitleCellSx';

type Props = {
  campaign: CampaignListItem;
  onClick: (campaign: Campaign | SearchCampaignItem) => void;
  addChildrenCampaignsToParentGroupCampaign: ({
    childrenCampaigns,
    campaignGroupId,
    isParentInOtherCampaigns
  }: {
    childrenCampaigns: Campaign[];
    campaignGroupId: string;
    isParentInOtherCampaigns: boolean | undefined;
  }) => void;
  removeChildrenCampaignsFromParentGroupCampaign: ({
    campaignGroupId,
    childrenCampaigns
  }: {
    campaignGroupId: string;
    childrenCampaigns: Campaign[];
  }) => void;
  isFiltering?: boolean;
};

const getCampaignHref = (campaign: CampaignListItem): string => {
  if (campaign.status === CampaignStatus.DRAFT) {
    return `${Paths.CAMPAIGN_CREATE_DRAFT}/${campaign.id}`;
  }

  return `${Paths.OUTBOUND_CAMPAIGN}/${campaign.id}`;
};

export const CampaignJobTitleCell: React.FC<Props> = ({
  campaign,
  onClick,
  addChildrenCampaignsToParentGroupCampaign,
  removeChildrenCampaignsFromParentGroupCampaign,
  isFiltering
}) => {
  const { title, campaignGroupId, isParent, isParentExpanded, type } = campaign;
  const isGroupParent = campaignGroupId && isParent;
  const isGroupChild = campaignGroupId && !isParent;
  const isParentInOtherCampaigns = type === CampaignType.ENGAGEMENT_OUTREACH;

  const { data, loading } = useGetCampaignsByCampaignGroupIdQuery({
    variables: {
      campaignGroupId: String(campaign.campaignGroupId)
    },
    skip: !isGroupParent,
    onCompleted: (data) => {
      const hasReview = hasCampaignInReview({
        campaigns: mapUserCampaignsToCampaigns(data.getCampaignsByCampaignGroup)
      });

      if (hasReview && campaignGroupId && !isParentExpanded) {
        onAddChildren(data);
      }
    },
    fetchPolicy: 'cache-and-network'
  });

  const onRemoveChildren = () => {
    if (!data?.getCampaignsByCampaignGroup || !campaign.campaignGroupId) return;

    const childrenCampaigns = mapUserCampaignsToCampaigns(
      data.getCampaignsByCampaignGroup
    );

    removeChildrenCampaignsFromParentGroupCampaign({
      campaignGroupId: campaign.campaignGroupId,
      childrenCampaigns
    });
  };

  const onAddChildren = (
    data: GetCampaignsByCampaignGroupIdQuery | undefined
  ) => {
    if (!data?.getCampaignsByCampaignGroup || !campaign.campaignGroupId) return;

    const childrenCampaigns = mapUserCampaignsToCampaigns(
      data.getCampaignsByCampaignGroup
    );

    addChildrenCampaignsToParentGroupCampaign({
      campaignGroupId: campaign.campaignGroupId,
      childrenCampaigns,
      isParentInOtherCampaigns
    });
  };

  const icon = () => {
    if (isParentExpanded) {
      return (
        <IconButton onClick={onRemoveChildren} sx={iconContainer}>
          <KeyboardArrowDownIcon fontSize="small" color="primary" />
        </IconButton>
      );
    }

    if (loading) {
      return (
        <Box sx={iconContainer} pr={1}>
          <Loading size={16} />
        </Box>
      );
    }

    return (
      <IconButton onClick={() => onAddChildren(data)} sx={iconContainer}>
        <KeyboardArrowRightIcon fontSize="small" color="primary" />
      </IconButton>
    );
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }

    e.preventDefault();
    onClick(campaign);
  };

  return (
    <Box display="flex" alignItems="center">
      {!isFiltering && isGroupParent && icon()}

      {!isFiltering && isGroupChild && <Box sx={{ width: 50 }} />}

      <AnchorText href={getCampaignHref(campaign)} onClick={handleClick}>
        {title}
      </AnchorText>

      {'workflowId' in campaign && campaign.workflowId && (
        <Tooltip
          title="View this campaign in your workflow"
          placement="top"
          arrow
          componentsProps={{
            tooltip: { sx: workflowTooltipSx },
            arrow: { sx: workflowTooltipArrowSx }
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              window.open(
                `${workflowBuilderLink}/${campaign.workflowId}`,
                '_blank'
              );
            }}
            sx={workflowIconButtonSx}
          >
            <WorkflowBuilderIcon size={14} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
